// frontend/src/pages/manufacturing/v2/components/AdminWOEditDialogV2.jsx
//
// Admin-only edit dialog for a v2 work order. Two independent concerns:
//
//   1. Safe-field PATCH — due_date, priority, notes. Maps to
//      v2.workOrders.adminUpdate(id, payload). Only changed fields are sent.
//   2. Revise quantity — a separate section that changes quantity_to_produce.
//      Maps to v2.workOrders.reviseQuantity(id, { quantity, notes }). A
//      top-level/manual WO cascades to its whole tree; a child (sub-assembly)
//      WO revises itself + its own sub-tree only (e.g. fixing a 1.99-should-
//      be-2 netting artifact). The API floors the new quantity at what's
//      already been produced. We only call it when the quantity actually
//      changed.
//
// Backend 400 messages are surfaced verbatim in the dialog's error state so
// admins see exactly why a revise/patch was rejected.

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Stack, Alert, Box, Typography, Divider, CircularProgress,
} from '@mui/material';
import { Edit, CheckCircle } from '@mui/icons-material';
import { v2 } from '../../../../services/api';

// Mirrors WorkOrderV2.PRIORITY_CHOICES in the backend model.
export const PRIORITY_CHOICES = [
  { value: 1, label: 'Urgent' },
  { value: 2, label: 'High' },
  { value: 3, label: 'Normal' },
  { value: 4, label: 'Low' },
];

// Extract a human message from a DRF 400 response. The backend returns either
// {"detail": "..."}, {"error": "..."}, {"quantity": ["..."]}, or a plain
// string — cover the common shapes.
function extractError(err) {
  const data = err?.response?.data;
  if (!data) return err?.message || 'Request failed';
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;
  if (data.error) return data.error;
  // Field errors: join the first array/message we find.
  const firstKey = Object.keys(data)[0];
  if (firstKey) {
    const v = data[firstKey];
    const msg = Array.isArray(v) ? v.join(' ') : String(v);
    return firstKey === 'non_field_errors' ? msg : `${firstKey}: ${msg}`;
  }
  return 'Request failed';
}

export default function AdminWOEditDialogV2({ open, wo, onClose, onSaved }) {
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState(3);
  const [notes, setNotes] = useState('');
  const [quantity, setQuantity] = useState('');
  const [reviseNotes, setReviseNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!wo) return;
    setDueDate(wo.due_date ? String(wo.due_date).slice(0, 10) : '');
    setPriority(wo.priority ?? 3);
    setNotes(wo.notes || '');
    setQuantity(String(wo.quantity_to_produce ?? ''));
    setReviseNotes('');
    setError(null);
  }, [wo]);

  if (!wo) return null;

  const isTopLevel = !!wo.is_top_level;
  const originalQty = parseFloat(wo.quantity_to_produce);
  const newQty = parseFloat(quantity);
  const quantityChanged = !Number.isNaN(newQty) && newQty !== originalQty;
  const alreadyProduced = parseFloat(
    wo.quantity_produced ?? wo.quantity_completed ?? 0,
  ) || 0;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // 1. Safe-field PATCH — only send changed fields.
      const payload = {};
      const origDue = wo.due_date ? String(wo.due_date).slice(0, 10) : '';
      if (dueDate !== origDue) payload.due_date = dueDate || null;
      if (priority !== (wo.priority ?? 3)) payload.priority = priority;
      if (notes !== (wo.notes || '')) payload.notes = notes;
      if (Object.keys(payload).length > 0) {
        await v2.workOrders.adminUpdate(wo.id, payload);
      }

      // 2. Revise quantity — only when it actually changed.
      if (quantityChanged) {
        await v2.workOrders.reviseQuantity(wo.id, {
          quantity: newQty,
          notes: reviseNotes,
        });
      }

      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error('Admin WO edit failed', err);
      setError(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Edit /> Edit Work Order: {wo.wo_number}
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="admin-edit-error">
            {error}
          </Alert>
        )}
        <Stack spacing={3} sx={{ mt: 2 }}>
          <TextField
            label="Due date"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'admin-edit-due-date' }}
            fullWidth
          />
          <TextField
            select
            label="Priority"
            value={priority}
            onChange={(e) => setPriority(Number(e.target.value))}
            SelectProps={{ inputProps: { 'data-testid': 'admin-edit-priority' } }}
            fullWidth
          >
            {PRIORITY_CHOICES.map((p) => (
              <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Notes"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            inputProps={{ 'data-testid': 'admin-edit-notes' }}
            fullWidth
          />

          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary">Revise quantity</Typography>
          </Divider>

          <TextField
            label="New quantity to produce"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            inputProps={{ min: 0, step: 1, 'data-testid': 'admin-edit-quantity' }}
            fullWidth
            helperText={
              isTopLevel
                ? `Children scale proportionally. Cannot go below already-produced (${alreadyProduced}).`
                : 'Sub-assembly WO: revises this WO and its own sub-tree only '
                  + '(reservations adjust to match). Cannot go below '
                  + `already-produced (${alreadyProduced}).`
            }
          />
          {quantityChanged && (
            <TextField
              label="Reason for revision (optional)"
              multiline
              rows={2}
              value={reviseNotes}
              onChange={(e) => setReviseNotes(e.target.value)}
              inputProps={{ 'data-testid': 'admin-edit-revise-notes' }}
              fullWidth
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} /> : <CheckCircle />}
          disabled={saving}
          data-testid="admin-edit-save"
        >
          Save changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
